import * as React from 'react'

import { Trash2Icon } from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import FilterFooter from '~/client/src/desktop/components/Filters/FilterFooter/FilterFooter'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { Loader } from '~/client/src/shared/components/Loader'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import {
  Radio,
  RadioGroup,
} from '~/client/src/shared/components/shadcn-ui/radio-group'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import BaseGroupingOptionsStore from '../../BaseGroupingOptions.store'
import CustomBandCreation from './CustomBandCreation'

// localization: translated

interface IProps {
  store: BaseGroupingOptionsStore
  state?: DesktopInitialState
  shouldHideBandCreation?: boolean
}

@inject('state')
@observer
export default class BaseGroupingOptionsPopup extends React.Component<IProps> {
  public componentDidMount() {
    this.props.store.initPopup()
  }

  public render() {
    const { shouldHideBandCreation } = this.props
    const {
      areDefaultOptionsSelected,
      resetGroupingOptions,
      changeSelectedBands,
      selectedBandsOptionIdDraft,
      applyGroupingOptions,
      hideGroupingOptions,
      leftCaptionKey,
      rightCaptionKey,
      shouldIndent,
      isLoading,
      bandsOptions,
      doesGroupingAlreadyExist,
    } = this.props.store

    return (
      <div className="col filter-container grouping-options-container no-outline-container">
        {isLoading && (
          <Loader
            size="lg"
            className="row x-center y-center full-height full-width absolute z-index-100 bg-white"
          />
        )}
        <div className="px20 py10 bb-light-grey grouping-options-container-body scrollable relative col">
          <Button
            variant="primary-outline"
            size="xs"
            className="tw:mb-2.5"
            disabled={areDefaultOptionsSelected}
            onClick={resetGroupingOptions}
          >
            {Localization.translator.reset}
          </Button>
          <div className="text bold large pb5 mb10 bb-light-grey">
            {Localization.getText(leftCaptionKey)}
            <span className="float-right">
              {rightCaptionKey && Localization.getText(rightCaptionKey)}
            </span>
          </div>
          <RadioGroup
            className="tw:mb-5 tw:pr-4 tw:flex tw:flex-col tw:gap-2 tw:relative tw:min-h-[130px] tw:overflow-auto"
            value={selectedBandsOptionIdDraft}
            onValueChange={changeSelectedBands}
          >
            {bandsOptions.map(
              ({
                id,
                name,
                nameTraslatorKey,
                num,
                disabled,
                canDelete,
                deletionIndex,
              }) => {
                const displayName = nameTraslatorKey
                  ? Localization.getText(nameTraslatorKey)
                  : name
                return (
                  <label
                    key={id + displayName + num}
                    className={classList({
                      'tw:inline-flex tw:items-center tw:gap-2 tw:cursor-pointer tw:select-none tw:text-body-sm': true,
                      'tw:opacity-50 tw:pointer-events-none': disabled,
                      'tw:pl-6': shouldIndent(id),
                    })}
                  >
                    <Radio value={id} disabled={disabled} />
                    <span className="tw:flex tw:items-center tw:justify-between tw:flex-1">
                      <span>{displayName}</span>
                      {this.renderAdditionalInfo(deletionIndex, num, canDelete)}
                    </span>
                  </label>
                )
              },
            )}
          </RadioGroup>
          {!shouldHideBandCreation && (
            <>
              <CustomBandCreation store={this.props.store} />
              {this.renderEmptyBandsSection()}
              {doesGroupingAlreadyExist && (
                <div className="mb10 text red">
                  {Localization.translator.thisGroupAlreadyExists}
                </div>
              )}
            </>
          )}
        </div>
        <FilterFooter
          clickOnApply={applyGroupingOptions}
          clickOnCancel={hideGroupingOptions}
        />
      </div>
    )
  }

  private renderEmptyBandsSection() {
    const {
      shouldShowEmptyBandsSection,
      showEmptyBandsDraft,
      onShowEmptyWbsChange,
    } = this.props.store
    if (!shouldShowEmptyBandsSection) {
      return null
    }

    return (
      <>
        <div className="text bold large pb5 mb10 bb-light-grey">
          {Localization.translator.groupByOptions}
        </div>
        <div className="mb10">
          <label className="tw:inline-flex tw:items-center tw:gap-2 tw:cursor-pointer tw:select-none">
            <Checkbox
              checked={showEmptyBandsDraft}
              onCheckedChange={() => onShowEmptyWbsChange()}
            />
            <span>{Localization.translator.showEmptyBandsLevels}</span>
          </label>
        </div>
      </>
    )
  }

  private renderAdditionalInfo(
    deletionIndex: number,
    num: number,
    canDelete: boolean,
  ): JSX.Element {
    const { isAdmin } = this.props.state.userActiveProjectSettings
    const { deleteCustomGrouping } = this.props.store

    if (isAdmin && canDelete) {
      return (
        <Button
          variant="destructive-ghost"
          size="icon-xs"
          aria-label={Localization.translator.delete}
          onClick={deleteCustomGrouping.bind(this, deletionIndex)}
        >
          <Trash2Icon />
        </Button>
      )
    }

    return null
  }
}
